import { db } from "../firebase/config";
import { collection, doc, addDoc, updateDoc, deleteDoc, onSnapshot, getDocs, query, where } from "firebase/firestore";
import { logSecurityEvent } from "./AuditLogger";

// Listen to all POs in real-time
export function subscribeToPOs(callback) {
  return onSnapshot(collection(db, "pos"), (snapshot) => {
    const list = [];
    snapshot.forEach((doc) => {
      list.push({ id: doc.id, ...doc.data() });
    });
    // Sort by PO number descending
    list.sort((a, b) => (b.poNumber || "").localeCompare(a.poNumber || ""));
    callback(list);
  }, (error) => {
    console.error("Error subscribing to POs:", error);
    callback([], error);
  });
}

// Add a new PO
export async function addPO(poData) {
  const data = {
    ...poData,
    updatedAt: poData.updatedAt || new Date().toISOString()
  };
  const docRef = await addDoc(collection(db, "pos"), data);
  return docRef.id;
}

// Update an existing PO
export async function updatePO(id, poData) {
  const docRef = doc(db, "pos", id);
  const data = {
    ...poData,
    updatedAt: new Date().toISOString()
  };
  await updateDoc(docRef, data);
}

// Delete a PO
export async function deletePO(id) {
  const docRef = doc(db, "pos", id);
  await deleteDoc(docRef);
}

// Ensure default POs are seeded if empty (Legacy mock seeding disabled)
export async function seedDefaultPOsIfEmpty(pos) {
  return;
}

/**
 * Deprecated: All estimates are retained regardless of request date.
 */
export function isPoOlderThanDate() {
  return false;
}

/**
 * Deprecated: Automated purging disabled to protect backdated and historical estimates.
 */
export async function purgeLegacyPOsBeforeDate() {
  return {
    success: true,
    purgedCount: 0,
    purgedPoNumbers: []
  };
}


/**
 * Permanently deletes an estimate (PO) and cascades deletion across all linked collections:
 * logistics, shipmentsHistory, inventory, tasks, and chats.
 * Emits a high-severity security audit event.
 */
export async function deleteEstimateCompletely(poId, poNumber, currentUser) {
  if (!poId) throw new Error("Estimate ID is required to delete.");

  try {
    // 1. Delete PO document from Firestore pos collection
    await deleteDoc(doc(db, "pos", poId));

    // 2. Cascade delete from logistics
    try {
      const logQ = query(collection(db, "logistics"), where("poId", "==", poId));
      const logSnap = await getDocs(logQ);
      for (const d of logSnap.docs) {
        await deleteDoc(doc(db, "logistics", d.id));
      }
      if (poNumber) {
        const logQ2 = query(collection(db, "logistics"), where("poNumber", "==", poNumber));
        const logSnap2 = await getDocs(logQ2);
        for (const d of logSnap2.docs) {
          await deleteDoc(doc(db, "logistics", d.id));
        }
      }
    } catch (e) {
      console.warn("Cascade delete logistics notice:", e);
    }

    // 3. Cascade delete from shipmentsHistory
    try {
      const histQ = query(collection(db, "shipmentsHistory"), where("poId", "==", poId));
      const histSnap = await getDocs(histQ);
      for (const d of histSnap.docs) {
        await deleteDoc(doc(db, "shipmentsHistory", d.id));
      }
      if (poNumber) {
        const histQ2 = query(collection(db, "shipmentsHistory"), where("poNumber", "==", poNumber));
        const histSnap2 = await getDocs(histQ2);
        for (const d of histSnap2.docs) {
          await deleteDoc(doc(db, "shipmentsHistory", d.id));
        }
      }
    } catch (e) {
      console.warn("Cascade delete shipmentsHistory notice:", e);
    }

    // 4. Cascade delete from inventory
    try {
      const invQ = query(collection(db, "inventory"), where("poId", "==", poId));
      const invSnap = await getDocs(invQ);
      for (const d of invSnap.docs) {
        await deleteDoc(doc(db, "inventory", d.id));
      }
      if (poNumber) {
        const invQ2 = query(collection(db, "inventory"), where("poNumber", "==", poNumber));
        const invSnap2 = await getDocs(invQ2);
        for (const d of invSnap2.docs) {
          await deleteDoc(doc(db, "inventory", d.id));
        }
      }
    } catch (e) {
      console.warn("Cascade delete inventory notice:", e);
    }

    // 5. Cascade delete from tasks
    try {
      const taskQ = query(collection(db, "tasks"), where("poId", "==", poId));
      const taskSnap = await getDocs(taskQ);
      for (const d of taskSnap.docs) {
        await deleteDoc(doc(db, "tasks", d.id));
      }
    } catch (e) {
      console.warn("Cascade delete tasks notice:", e);
    }

    // 6. Cascade delete from chats
    try {
      const chatQ = query(collection(db, "chats"), where("poId", "==", poId));
      const chatSnap = await getDocs(chatQ);
      for (const d of chatSnap.docs) {
        await deleteDoc(doc(db, "chats", d.id));
      }
    } catch (e) {
      console.warn("Cascade delete chats notice:", e);
    }

    // 7. Compliance center audit log
    await logSecurityEvent({
      action: "DELETE_ESTIMATE_COMPLETELY",
      category: "DATA_MODIFICATION",
      userId: currentUser?.uid || "unknown",
      userEmail: currentUser?.email || "unknown",
      resource: `pos/${poId}`,
      status: "SUCCESS",
      severity: "HIGH",
      details: {
        poId,
        poNumber: poNumber || poId,
        deletedAt: new Date().toISOString()
      }
    });

    return { success: true };
  } catch (error) {
    console.error("Error deleting estimate completely:", error);
    await logSecurityEvent({
      action: "DELETE_ESTIMATE_COMPLETELY",
      category: "DATA_MODIFICATION",
      userId: currentUser?.uid || "unknown",
      userEmail: currentUser?.email || "unknown",
      resource: `pos/${poId}`,
      status: "FAILURE",
      severity: "HIGH",
      details: {
        poId,
        poNumber: poNumber || poId,
        error: error.message
      }
    });
    throw error;
  }
}
